import React, { useState, useEffect, useRef } from 'react';

const EmbeddableSquirrelMascot = ({ width = 200, height = 200 }) => {
  // Simplified state for minimal animations
  const [blinking, setBlinking] = useState(false);
  const [tailWagging, setTailWagging] = useState(false);
  const [idleMovement, setIdleMovement] = useState(0);
  const [breathing, setBreathing] = useState(0);
  const [headTilt, setHeadTilt] = useState(0);
  const [bounce, setBounce] = useState(0);
  const breathingRef = useRef(0);
  
  // For tail animation
  const animationFrameRef = useRef();
  const tailWagCount = useRef(0);
  
  // No messages, keep empty array
  const messages = [];

  // Initialize breathing animation
  useEffect(() => {
    const breathingInterval = setInterval(() => {
      breathingRef.current = Math.sin(Date.now() / 1000) * 2;
      setBreathing(breathingRef.current);
    }, 50);

    return () => clearInterval(breathingInterval);
  }, []);

  // Minimal bouncing effect
  useEffect(() => {
    const bounceInterval = setInterval(() => {
      setBounce(Math.sin(Date.now() / 700) * 1.5);
    }, 50);

    return () => clearInterval(bounceInterval);
  }, []);

  // Blinking behavior
  useEffect(() => {
    const randomBlinkInterval = () => Math.random() * 3000 + 800;
    
    const triggerBlink = () => {
      setBlinking(true);
      setTimeout(() => {
        setBlinking(false);
        blinkTimeoutRef.current = setTimeout(triggerBlink, randomBlinkInterval());
      }, 200);
    };
    
    const blinkTimeoutRef = { current: null };
    blinkTimeoutRef.current = setTimeout(triggerBlink, randomBlinkInterval());
    
    return () => {
      clearTimeout(blinkTimeoutRef.current);
    };
  }, []);

  // Occasional tail wagging (around every 15 seconds)
  useEffect(() => {
    const wagInterval = setInterval(() => {
      if (Math.random() < 0.8 && !tailWagging) { // High probability to ensure ~15 second frequency
        startTailWagging();
      }
    }, 15000);

    return () => clearInterval(wagInterval);
  }, [tailWagging]);

  // Idle movement for minimal liveliness
  useEffect(() => {
    const idleInterval = setInterval(() => {
      setIdleMovement(Math.sin(Date.now() / 2000) * 2);
      setHeadTilt(Math.sin(Date.now() / 3000) * 3);
    }, 50);

    return () => clearInterval(idleInterval);
  }, []);

  // Tail wagging animation
  const startTailWagging = () => {
    if (tailWagging) return;
    
    setTailWagging(true);
    tailWagCount.current = 0;
    
    const wagTail = () => {
      tailWagCount.current += 1;
      if (tailWagCount.current > 20) {
        setTailWagging(false);
        return;
      }
      animationFrameRef.current = requestAnimationFrame(wagTail);
    };
    
    animationFrameRef.current = requestAnimationFrame(wagTail);
  };

  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  // Animation values
  const tailWagRotation = tailWagging ? Math.sin(Date.now() / 50) * 25 : 0;
  
  return (
    <div 
      className="relative flex items-center justify-center"
      style={{ 
        width: `${width}px`, 
        height: `${height}px` 
      }}
    >
      {/* Main squirrel container with minimal movement */}
      <div 
        className="relative transform transition-transform"
        style={{ 
          transform: `translateY(${idleMovement + bounce}px)`,
          transition: 'transform 0.5s ease',
          width: '100%',
          height: '100%'
        }}
      >
        {/* SVG Squirrel - with responsive sizing */}
        <svg 
          viewBox="0 0 300 300" 
          width="100%" 
          height="100%"
          className="overflow-visible"
        >
          {/* Tail */}
          <g 
            style={{ 
              transformOrigin: '110px 150px', 
              transform: `rotate(${tailWagRotation}deg)`,
              transition: tailWagging ? 'none' : 'transform 0.5s ease'
            }}
          >
            <path 
              d="M110,150 C70,90 30,90 20,140 C10,190 60,200 100,170" 
              fill="#A9A9A9" 
              stroke="#888" 
              strokeWidth="1"
            />
            <path 
              d="M110,150 C70,110 40,140 50,180 C60,210 90,200 100,180" 
              fill="#B8B8B8" 
              stroke="#888" 
              strokeWidth="1"
            />
            <path 
              d="M100,170 C120,150 130,180 105,190" 
              fill="#B8B8B8" 
              stroke="#888" 
              strokeWidth="1"
            />
            <path 
              d="M90,160 C60,140 40,160 50,170 C70,190 80,180 90,160" 
              fill="#CDCDCD" 
              stroke="#BBBBBB" 
              strokeWidth="1"
            />
          </g>
          
          {/* Body with breathing effect */}
          <ellipse 
            cx="150" 
            cy="150" 
            rx={45 + breathing} 
            ry={55 + breathing} 
            fill="#A9A9A9" 
            stroke="#888" 
            strokeWidth="1.5"
          />
          
          {/* Lighter belly */}
          <ellipse 
            cx="150" 
            cy="160" 
            rx={35 + breathing * 0.8} 
            ry={45 + breathing * 0.8} 
            fill="#E0E0E0"
          />
          
          {/* Head with slight tilt */}
          <g style={{ transformOrigin: '150px 100px', transform: `rotate(${headTilt}deg)` }}>
            <circle 
              cx="150" 
              cy="100" 
              r="50" 
              fill="#A9A9A9" 
              stroke="#888" 
              strokeWidth="1.5"
            />
            
            {/* Cheek fluff */}
            <ellipse 
              cx="125" 
              cy="115" 
              rx="20" 
              ry="15" 
              fill="#CDCDCD" 
              stroke="#CDCDCD" 
              strokeWidth="1"
              opacity="0.7"
            />
            <ellipse 
              cx="175" 
              cy="115" 
              rx="20" 
              ry="15" 
              fill="#CDCDCD" 
              stroke="#CDCDCD" 
              strokeWidth="1"
              opacity="0.7"
            />
            
            {/* Subtle blush */}
            <ellipse 
              cx="125" 
              cy="115" 
              rx="15" 
              ry="10" 
              fill="#FFB6C1" 
              opacity="0.2"
            />
            <ellipse 
              cx="175" 
              cy="115" 
              rx="15" 
              ry="10" 
              fill="#FFB6C1" 
              opacity="0.2"
            />
            
            {/* Ears */}
            <ellipse 
              cx="120" 
              cy="65" 
              rx="20" 
              ry="25" 
              fill="#A9A9A9" 
              stroke="#888" 
              strokeWidth="1.5"
            />
            <ellipse 
              cx="180" 
              cy="65" 
              rx="20" 
              ry="25" 
              fill="#A9A9A9" 
              stroke="#888" 
              strokeWidth="1.5"
            />
            
            {/* Inner ears */}
            <ellipse 
              cx="120" 
              cy="65" 
              rx="10" 
              ry="15" 
              fill="#D8D8D8"
            />
            <ellipse 
              cx="180" 
              cy="65" 
              rx="10" 
              ry="15" 
              fill="#D8D8D8"
            />
            
            {/* Eyes */}
            <ellipse 
              cx="130" 
              cy="95" 
              rx={blinking ? 13 * 0.1 : 13} 
              ry={blinking ? 15 * 0.1 : 15} 
              fill="white"
              style={{ transition: blinking ? 'none' : 'all 0.3s ease' }}
            />
            <ellipse 
              cx="170" 
              cy="95" 
              rx={blinking ? 13 * 0.1 : 13} 
              ry={blinking ? 15 * 0.1 : 15} 
              fill="white"
              style={{ transition: blinking ? 'none' : 'all 0.3s ease' }}
            />
            
            {/* Pupils */}
            <circle 
              cx="130" 
              cy="95" 
              r="8" 
              fill="black"
            />
            <circle 
              cx="170" 
              cy="95" 
              r="8" 
              fill="black"
            />
            
            {/* Eye shine */}
            <circle 
              cx="127" 
              cy="92" 
              r="4" 
              fill="white"
              style={{ display: blinking ? 'none' : 'block' }}
            />
            <circle 
              cx="167" 
              cy="92" 
              r="4" 
              fill="white"
              style={{ display: blinking ? 'none' : 'block' }}
            />
            
            {/* Secondary shine */}
            <circle 
              cx="133" 
              cy="96" 
              r="2" 
              fill="white"
              style={{ display: blinking ? 'none' : 'block' }}
            />
            <circle 
              cx="173" 
              cy="96" 
              r="2" 
              fill="white"
              style={{ display: blinking ? 'none' : 'block' }}
            />
            
            {/* Nose */}
            <ellipse 
              cx="150" 
              cy="110" 
              rx="10" 
              ry="7" 
              fill="#555"
            />
            
            {/* Happy mouth */}
            <path 
              d="M135,125 Q150,132 165,125" 
              stroke="#555" 
              strokeWidth="2.5" 
              strokeLinecap="round"
              fill="none"
            />
            
            {/* Whiskers */}
            <line x1="135" y1="112" x2="110" y2="106" stroke="#777" strokeWidth="1.2" />
            <line x1="135" y1="115" x2="110" y2="115" stroke="#777" strokeWidth="1.2" />
            <line x1="135" y1="118" x2="110" y2="124" stroke="#777" strokeWidth="1.2" />
            <line x1="165" y1="112" x2="190" y2="106" stroke="#777" strokeWidth="1.2" />
            <line x1="165" y1="115" x2="190" y2="115" stroke="#777" strokeWidth="1.2" />
            <line x1="165" y1="118" x2="190" y2="124" stroke="#777" strokeWidth="1.2" />
          </g>
          
          {/* Paws */}
          <ellipse 
            cx="120" 
            cy="160" 
            rx="18" 
            ry="12" 
            fill="#A9A9A9" 
            stroke="#888" 
            strokeWidth="1.5"
          />
          <ellipse 
            cx="180" 
            cy="160" 
            rx="18" 
            ry="12" 
            fill="#A9A9A9" 
            stroke="#888" 
            strokeWidth="1.5"
          />
          
          {/* Feet */}
          <ellipse 
            cx="125" 
            cy="200" 
            rx="18" 
            ry="12" 
            fill="#A9A9A9" 
            stroke="#888" 
            strokeWidth="1.5"
          />
          <ellipse 
            cx="175" 
            cy="200" 
            rx="18" 
            ry="12" 
            fill="#A9A9A9" 
            stroke="#888" 
            strokeWidth="1.5"
          />
        </svg>
      </div>
    </div>
  );
};

export default EmbeddableSquirrelMascot;